import { useRef, useState, useEffect, useContext } from "react";
import readNDJSONStream from "ndjson-readablestream";

import {
    chatApi,
    configApi,
    RetrievalMode,
    logDevChoice,
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    ResponseMessage,
    SpeechConfig
} from "../api";
import { AnalysisPanelTabs } from "../components/AnalysisPanel";
import { HistoryProviderOptions, useHistoryManager } from "../components/HistoryProviders";
import { useLogin, getToken, requireAccessControl } from "../authConfig";
import { useMsal } from "@azure/msal-react";
import { LoginContext } from "../loginContext";
import { useClinicalFilters } from "../clinicalContext";
import { useSearchParams } from "react-router-dom";
import { useAuditLog } from "../hooks/useAuditLog";
import { ContentMode } from "../components/ContentModeSelector";
import { SHOW_SUMMARIES } from "../featureFlags";
import { TokenEstimate, BudgetOption } from "../components/TokenBudgetIndicator";
import { useTranslation } from "react-i18next";

export interface ModelInfo {
    name: string;
    modelId?: string;
    maxTokens: number;
    isCurrent: boolean;
    isRecommended?: boolean;
    providerType?: string;
    providerName?: string;
}

type ChatAnswer = [user: string, response: ChatAppResponse];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isChatAppResponse(value: unknown): value is ChatAppResponse {
    return isRecord(value) && isRecord(value.message) && typeof value.message.content === "string";
}

function normalizeHistoryAnswers(value: unknown): ChatAnswer[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap(item => {
        if (Array.isArray(item) && typeof item[0] === "string" && isChatAppResponse(item[1])) {
            return [[item[0], item[1]] as ChatAnswer];
        }

        if (isRecord(item)) {
            const question = typeof item.question === "string" ? item.question : typeof item.user === "string" ? item.user : undefined;
            const response = item.response;
            if (question && isChatAppResponse(response)) {
                return [[question, response] as ChatAnswer];
            }
        }

        return [];
    });
}

function getSessionStateId(sessionState: unknown): string | null {
    if (typeof sessionState === "string" && sessionState.trim()) return sessionState;
    if (!isRecord(sessionState)) return null;

    const id = sessionState.session_id ?? sessionState.id;
    return typeof id === "string" && id.trim() ? id : null;
}

export function useChatSession(options?: { defaultContentMode?: ContentMode; compact?: boolean }) {
    const { logQuestion } = useAuditLog("Chat");
    const { t, i18n } = useTranslation();

    // Models & templates
    const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
    const [selectedModel, setSelectedModel] = useState<string>("");
    const [selectedTemplate, setSelectedTemplate] = useState<string>("clinical_general");
    const [contentMode, setContentMode] = useState<ContentMode>(SHOW_SUMMARIES ? (options?.defaultContentMode ?? "full") : "full");
    const [tokenEstimate, setTokenEstimate] = useState<TokenEstimate | null>(null);
    const [isEstimatingTokens, setIsEstimatingTokens] = useState(false);

    // Context
    const { userId: entraUserId, ehrUserId } = useContext(LoginContext);
    const {
        filters: clinicalFilters,
        setFilters: setClinicalFilters,
        caseloadSelection,
        chatAnswers: answers,
        setChatAnswers: setAnswers,
        chatLastQuestion,
        setChatLastQuestion,
        selectedNoteScope,
        clearSelectedNoteScope,
        setSelectedNoteScope
    } = useClinicalFilters();
    const [searchParams] = useSearchParams();

    // Panel state (exposed so layouts can control panels)
    const [isClinicalPanelOpen, setIsClinicalPanelOpen] = useState(false);
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);

    // Dev settings
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [temperature, setTemperature] = useState<number>(0);
    const [seed, setSeed] = useState<number | null>(null);
    const [minimumRerankerScore, setMinimumRerankerScore] = useState<number>(0);
    const [minimumSearchScore, setMinimumSearchScore] = useState<number>(0);
    const [agenticReasoningEffort, setRetrievalReasoningEffort] = useState<string>("minimal");
    const [retrievalMode, setRetrievalMode] = useState<RetrievalMode>(RetrievalMode.Hybrid);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useQueryRewriting, setUseQueryRewriting] = useState<boolean>(false);
    const [reasoningEffort, setReasoningEffort] = useState<string>("");
    const [streamingEnabled, setStreamingEnabled] = useState<boolean>(true);
    const [shouldStream, setShouldStream] = useState<boolean>(true);
    const previousShouldStreamRef = useRef<boolean>(true);
    const forcedStreamingRef = useRef<boolean>(false);
    const [includeCategory, setIncludeCategory] = useState<string>("");
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [searchTextEmbeddings, setSearchTextEmbeddings] = useState<boolean>(true);
    const [searchImageEmbeddings, setSearchImageEmbeddings] = useState<boolean>(false);
    const [sendTextSources, setSendTextSources] = useState<boolean>(true);
    const [sendImageSources, setSendImageSources] = useState<boolean>(false);

    // Refs
    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatMessageStreamContainer = useRef<HTMLDivElement | null>(null);
    const streamAutoScrollEnabled = useRef<boolean>(true);

    // Loading state
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();
    const [isClinicalPanelPinned, setIsClinicalPanelPinned] = useState(false);

    // Analysis panel
    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);

    // Restore lastQuestionRef from context on mount
    useEffect(() => {
        if (chatLastQuestion) {
            lastQuestionRef.current = chatLastQuestion;
        }
    }, []);

    const [streamedAnswers, setStreamedAnswers] = useState<[user: string, response: ChatAppResponse][]>([]);
    const [speechUrls, setSpeechUrls] = useState<(string | null)[]>([]);

    // Settings change confirmation dialog
    const [pendingSettingChange, setPendingSettingChange] = useState<{ type: string; apply: () => void } | null>(null);
    const hasChatInProgress = answers.length > 0;
    const selectedNoteScopeKey = JSON.stringify({
        status: selectedNoteScope.status,
        mode: selectedNoteScope.mode,
        noteIds: selectedNoteScope.noteIds,
        patientIds: selectedNoteScope.patientIds,
        sourceFilters: selectedNoteScope.sourceFilters,
        conflictReason: selectedNoteScope.conflictReason
    });
    const previousSelectedNoteScopeKey = useRef(selectedNoteScopeKey);

    // Feature flags
    const [showMultimodalOptions, setShowMultimodalOptions] = useState<boolean>(false);
    const [showSemanticRankerOption, setShowSemanticRankerOption] = useState<boolean>(false);
    const [showQueryRewritingOption, setShowQueryRewritingOption] = useState<boolean>(false);
    const [showReasoningEffortOption, setShowReasoningEffortOption] = useState<boolean>(false);
    const [showVectorOption, setShowVectorOption] = useState<boolean>(false);
    const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
    const [showLanguagePicker, setshowLanguagePicker] = useState<boolean>(false);
    const [showSpeechInput, setShowSpeechInput] = useState<boolean>(false);
    const [showSpeechOutputBrowser, setShowSpeechOutputBrowser] = useState<boolean>(false);
    const [showSpeechOutputAzure, setShowSpeechOutputAzure] = useState<boolean>(false);
    const [showChatHistoryBrowser, setShowChatHistoryBrowser] = useState<boolean>(false);
    const [showChatHistoryCosmos, setShowChatHistoryCosmos] = useState<boolean>(false);
    const [showAgenticRetrievalOption, setShowAgenticRetrievalOption] = useState<boolean>(false);
    const [webSourceSupported, setWebSourceSupported] = useState<boolean>(false);
    const [webSourceEnabled, setWebSourceEnabled] = useState<boolean>(false);
    const [sharePointSourceSupported, setSharePointSourceSupported] = useState<boolean>(false);
    const [sharePointSourceEnabled, setSharePointSourceEnabled] = useState<boolean>(false);
    const [useAgenticKnowledgeBase, setUseAgenticRetrieval] = useState<boolean>(false);
    const [useLangGraphClinicalRetrieval, setUseLangGraphClinicalRetrieval] = useState<boolean>(true);
    const [clinicalChatGraphMode, setClinicalChatGraphMode] = useState<"off" | "trace" | "supplemental" | "fusion">("off");
    const [clinicalChatQueryRewriteMode, setClinicalChatQueryRewriteMode] = useState<"auto" | "deterministic" | "llm">("auto");
    const [clinicalChatRuntimePlannerMode, setClinicalChatRuntimePlannerMode] = useState<"deterministic" | "llm">("deterministic");
    const [clinicalChatRerankerMode, setClinicalChatRerankerMode] = useState<"disabled" | "lexical">("disabled");
    const [showGraphSupplementalEvidenceOption, setShowGraphSupplementalEvidenceOption] = useState<boolean>(false);
    const [showGraphSupplementalEvidence, setShowGraphSupplementalEvidence] = useState<boolean>(false);
    const [useBudgetMonitor, setUseBudgetMonitor] = useState<boolean>(true);
    const [contextPerspective, setContextPerspective] = useState<"focused_rag" | "whole_patient_review">("focused_rag");
    const [retrievalEffort, setRetrievalEffort] = useState<"quick" | "balanced" | "best">("balanced");
    const [hideMinimalRetrievalReasoningOption, setHideMinimalRetrievalReasoningOption] = useState<boolean>(false);
    const streamingDisabledByOverrides = useAgenticKnowledgeBase && webSourceEnabled;

    // Audio / speech
    const audio = useRef(new Audio()).current;
    const [isPlaying, setIsPlaying] = useState(false);

    const speechConfig: SpeechConfig = {
        speechUrls,
        setSpeechUrls,
        audio,
        isPlaying,
        setIsPlaying
    };

    // Auth
    const client = useLogin ? useMsal().instance : undefined;
    const { loggedIn } = useContext(LoginContext);

    // History
    const historyProvider: HistoryProviderOptions = (() => {
        if (showChatHistoryCosmos) return HistoryProviderOptions.CosmosDB;
        if (showChatHistoryBrowser) return HistoryProviderOptions.IndexedDB;
        return HistoryProviderOptions.None;
    })();
    const historyManager = useHistoryManager(historyProvider);

    const saveHistory = async (nextAnswers: ChatAnswer[], response: ChatAppResponse) => {
        const currentSessionState = answers.length ? answers[answers.length - 1][1].session_state : null;
        const sessionState = getSessionStateId(response.session_state) ?? getSessionStateId(currentSessionState);
        if (!sessionState) return;

        try {
            const token = client ? await getToken(client) : undefined;
            console.log("History save:", { entraUserId, ehrUserId, patId: clinicalFilters.patId, turns: nextAnswers.length });
            await historyManager.addItem(sessionState, nextAnswers, token, entraUserId, ehrUserId, clinicalFilters.patId);
        } catch (historyError) {
            console.error("History save failed:", historyError);
        }
    };

    const isNearDocumentBottom = () => {
        const container = chatMessageStreamContainer.current;
        if (container) {
            return container.scrollHeight - container.scrollTop - container.clientHeight < 180;
        }
        if (typeof window === "undefined" || typeof document === "undefined") return true;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const viewportBottom = scrollTop + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        return documentHeight - viewportBottom < 180;
    };

    const scrollToStreamEnd = (behavior: ScrollBehavior) => {
        if (!streamAutoScrollEnabled.current) return;
        const container = chatMessageStreamContainer.current;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior });
            return;
        }
        chatMessageStreamEnd.current?.scrollIntoView({ behavior });
    };

    const onChatMessageStreamScroll = () => {
        streamAutoScrollEnabled.current = isNearDocumentBottom();
    };

    // Fetch models
    useEffect(() => {
        fetch("/models")
            .then(res => res.json())
            .then(data => {
                setAvailableModels(data.models);
                const recommended = data.models.find((m: any) => m.isRecommended);
                setSelectedModel(recommended ? (recommended.modelId || recommended.name) : data.currentModel);
            })
            .catch(err => console.error("Failed to fetch models:", err));
    }, []);

    // Fetch config
    const getConfig = async () => {
        configApi().then(config => {
            setShowMultimodalOptions(config.showMultimodalOptions);
            if (config.showMultimodalOptions) {
                setSendTextSources(config.ragSendTextSources !== undefined ? config.ragSendTextSources : true);
                setSendImageSources(config.ragSendImageSources);
                setSearchTextEmbeddings(config.ragSearchTextEmbeddings);
                setSearchImageEmbeddings(config.ragSearchImageEmbeddings);
            }
            setUseSemanticRanker(config.showSemanticRankerOption);
            setShowSemanticRankerOption(config.showSemanticRankerOption);
            setUseQueryRewriting(config.showQueryRewritingOption);
            setShowQueryRewritingOption(config.showQueryRewritingOption);
            setShowReasoningEffortOption(config.showReasoningEffortOption);
            setStreamingEnabled(config.streamingEnabled);
            if (config.showReasoningEffortOption) {
                setReasoningEffort(config.defaultReasoningEffort);
            }
            setShowVectorOption(config.showVectorOption);
            if (!config.showVectorOption) {
                setRetrievalMode(RetrievalMode.Text);
            }
            setShowUserUpload(config.showUserUpload);
            setshowLanguagePicker(config.showLanguagePicker);
            setShowSpeechInput(config.showSpeechInput);
            setShowSpeechOutputBrowser(config.showSpeechOutputBrowser);
            setShowSpeechOutputAzure(config.showSpeechOutputAzure);
            setShowChatHistoryBrowser(config.showChatHistoryBrowser);
            setShowChatHistoryCosmos(config.showChatHistoryCosmos);
            setShowAgenticRetrievalOption(config.showAgenticRetrievalOption);
            setUseAgenticRetrieval(config.showAgenticRetrievalOption);
            setUseBudgetMonitor(!config.showAgenticRetrievalOption);
            setWebSourceSupported(config.webSourceEnabled);
            setWebSourceEnabled(config.webSourceEnabled);
            setSharePointSourceSupported(config.sharepointSourceEnabled);
            setSharePointSourceEnabled(config.sharepointSourceEnabled);
            setClinicalChatGraphMode(config.defaultClinicalChatGraph ?? "off");
            setClinicalChatQueryRewriteMode(config.defaultClinicalChatQueryRewrite ?? "auto");
            setClinicalChatRuntimePlannerMode(config.defaultClinicalChatRuntimePlanner ?? "deterministic");
            setClinicalChatRerankerMode(config.defaultClinicalChatReranker ?? "disabled");
            setShowGraphSupplementalEvidenceOption(config.graphSupplementalEvidenceUiEnabled);
            if (!config.graphSupplementalEvidenceUiEnabled) {
                setShowGraphSupplementalEvidence(false);
            }
            if (config.showAgenticRetrievalOption) {
                setClinicalFilters(prev => ({ ...prev, topResults: 10 }));
            }
            const defaultRetrievalEffort = config.defaultRetrievalReasoningEffort ?? "minimal";
            setHideMinimalRetrievalReasoningOption(config.webSourceEnabled);
            setRetrievalReasoningEffort(defaultRetrievalEffort);
        });
    };

    // Streaming preference management
    const updateStreamingPreference = (isStreamingEnabledOverride: boolean, disablesStreamingOverride: boolean) => {
        if (!isStreamingEnabledOverride) {
            setShouldStream(current => {
                if (!forcedStreamingRef.current) {
                    previousShouldStreamRef.current = current;
                }
                forcedStreamingRef.current = true;
                return current ? false : current;
            });
            return;
        }

        if (disablesStreamingOverride) {
            setShouldStream(current => {
                if (!forcedStreamingRef.current) {
                    previousShouldStreamRef.current = current;
                }
                forcedStreamingRef.current = true;
                return current ? false : current;
            });
            return;
        }

        forcedStreamingRef.current = false;
        setShouldStream(current => {
            const desiredShouldStream = previousShouldStreamRef.current;
            return current === desiredShouldStream ? current : desiredShouldStream;
        });
    };

    // Streaming handler
    const handleAsyncRequest = async (question: string, answers: [string, ChatAppResponse][], responseBody: ReadableStream<any>, onFirstToken?: () => void) => {
        let answer: string = "";
        let askResponse: ChatAppResponse = {} as ChatAppResponse;
        let firstTokenFired = false;

        const updateStreamedAnswer = () => {
            const latestResponse: ChatAppResponse = {
                ...askResponse,
                message: { content: answer, role: askResponse.message?.role || "assistant" }
            };
            setStreamedAnswers([...answers, [question, latestResponse]]);
        };

        const updateState = (newContent: string) => {
            return new Promise(resolve => {
                setTimeout(() => {
                    answer += newContent;
                    updateStreamedAnswer();
                    resolve(null);
                }, 33);
            });
        };
        try {
            setIsStreaming(true);
            for await (const event of readNDJSONStream(responseBody)) {
                if (event["context"] && event["context"]["data_points"]) {
                    event["message"] = event["delta"];
                    askResponse = event as ChatAppResponse;
                } else if (event["delta"] && event["delta"]["content"]) {
                    if (!firstTokenFired) {
                        firstTokenFired = true;
                        onFirstToken?.();
                    }
                    setIsLoading(false);
                    await updateState(event["delta"]["content"]);
                } else if (event["context"]) {
                    const eventContext = event["context"];
                    const nestedContext = eventContext["context"] && typeof eventContext["context"] === "object" ? eventContext["context"] : undefined;
                    const { context: _nestedContext, ...eventContextWithoutNested } = eventContext;
                    askResponse.context = {
                        ...askResponse.context,
                        ...nestedContext,
                        ...eventContextWithoutNested
                    };
                    if (event["session_state"] !== undefined) {
                        askResponse.session_state = event["session_state"];
                    }
                    updateStreamedAnswer();
                } else if (event["error"]) {
                    throw Error(event["error"]);
                }
            }
        } finally {
            setIsStreaming(false);
        }
        if (!answer.trim()) {
            throw Error("Model returned an empty response. Please retry or choose a different model.");
        }
        const fullResponse: ChatAppResponse = {
            ...askResponse,
            message: { content: answer, role: askResponse.message?.role || "assistant" }
        };
        return fullResponse;
    };

    // Core API request
    const makeApiRequest = async (question: string) => {
        const requestStartTime = Date.now();
        let firstTokenTime: number | null = null;

        const hasPatient = clinicalFilters.patId || (!SHOW_SUMMARIES && caseloadSelection.selectedPatients.length > 0);
        if (!hasPatient) {
            lastQuestionRef.current = question;
            setChatLastQuestion(question);
            const guidance = !SHOW_SUMMARIES
                ? "Please select one patient from the Caseload page."
                : "Please select a person from the Clinical Settings panel.";
            setAnswers([
                ...answers,
                [
                    question,
                    {
                        message: { content: guidance, role: "assistant" },
                        context: {
                            data_points: { text: [], images: [], citations: [] },
                            citations: [],
                            followup_questions: [],
                            thoughts: []
                        },
                        delta: { content: "", role: "assistant" },
                        session_state: null
                    } as ChatAppResponse
                ]
            ]);
            setIsLoading(false);
            return;
        }

        // Budget guard
        if (useBudgetMonitor && (tokenEstimate?.exceedsLimit || tokenEstimate?.budgetStatus === "exceeded" || tokenEstimate?.semanticLimitExceeded)) {
            const budgetMessage = tokenEstimate?.semanticLimitExceeded
                ? `⚠️ Your current selection (${tokenEstimate?.requestedCount ?? "?"} notes) exceeds the Snippets mode limit of ${tokenEstimate?.semanticLimit ?? 50} notes. Please reduce your selection using filters or switch content mode before asking a question.`
                : `⚠️ Your current selection exceeds the model's context window (${Math.round(tokenEstimate?.utilizationPercent ?? 0)}% utilization). Please reduce notes via filters, switch to Summaries/Snippets mode, or select a recommended option from the budget indicator.`;
            lastQuestionRef.current = question;
            setChatLastQuestion(question);
            setAnswers([
                ...answers,
                [
                    question,
                    {
                        message: { content: budgetMessage, role: "assistant" },
                        context: {
                            data_points: { text: [], images: [], citations: [] },
                            citations: [],
                            followup_questions: [],
                            thoughts: []
                        },
                        delta: { content: "", role: "assistant" },
                        session_state: null
                    } as ChatAppResponse
                ]
            ]);
            setIsLoading(false);
            return;
        }

        lastQuestionRef.current = question;
        setChatLastQuestion(question);
        logQuestion(question);

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        const token = client ? await getToken(client) : undefined;

        try {
            const messages: ResponseMessage[] = answers.flatMap(a => {
                const assistantContent = a[1].message.content?.trim();
                if (!assistantContent) {
                    return [];
                }
                return [
                    { content: a[0], role: "user" },
                    { content: assistantContent, role: "assistant" }
                ];
            });
            const clinicalTopOverride =
                contentMode === "semantic"
                    ? Math.min(clinicalFilters.topResults === 0 ? 50 : clinicalFilters.topResults || 50, 50)
                    : contextPerspective === "whole_patient_review" && clinicalFilters.topResults === 0
                      ? 0
                      : clinicalFilters.topResults === 0
                        ? undefined
                        : clinicalFilters.topResults;
            const currentSessionState = answers.length ? answers[answers.length - 1][1].session_state : null;
            const currentSessionStateId = getSessionStateId(currentSessionState);

            const request: ChatAppRequest = {
                messages: [...messages, { content: question, role: "user" }],
                context: {
                    overrides: {
                        prompt_template: promptTemplate.length === 0 ? undefined : promptTemplate,
                        prompt_template_name: selectedTemplate || undefined,
                        include_category: includeCategory.length === 0 ? undefined : includeCategory,
                        exclude_category: excludeCategory.length === 0 ? undefined : excludeCategory,
                        ...(useAgenticKnowledgeBase ? { retrieval_reasoning_effort: agenticReasoningEffort } : {}),
                        temperature: temperature,
                        minimum_reranker_score: minimumRerankerScore,
                        minimum_search_score: minimumSearchScore,
                        retrieval_mode: retrievalMode,
                        semantic_ranker: contentMode === "semantic" || useSemanticRanker,
                        semantic_captions: contentMode === "semantic",
                        query_rewriting: useQueryRewriting,
                        reasoning_effort: reasoningEffort,
                        suggest_followup_questions: useSuggestFollowupQuestions,
                        search_text_embeddings: searchTextEmbeddings,
                        search_image_embeddings: searchImageEmbeddings,
                        send_text_sources: sendTextSources,
                        send_image_sources: sendImageSources,
                        language: i18n.language,
                        use_agentic_knowledgebase: useAgenticKnowledgeBase,
                        ...(useLangGraphClinicalRetrieval ? {} : { agentic_clinical_retrieval: false }),
                        use_web_source: webSourceSupported ? webSourceEnabled : false,
                        use_sharepoint_source: sharePointSourceSupported ? sharePointSourceEnabled : false,
                        clinical_chat_graph: clinicalChatGraphMode,
                        clinical_chat_query_rewrite: clinicalChatQueryRewriteMode,
                        clinical_chat_runtime_planner: clinicalChatRuntimePlannerMode,
                        experimental_reranker: clinicalChatRerankerMode,
                        ...(showGraphSupplementalEvidenceOption && showGraphSupplementalEvidence ? { show_graph_supplemental_evidence: true } : {}),
                        consumer_id: clinicalFilters.patId || undefined,
                        consumer_ids: !SHOW_SUMMARIES && caseloadSelection.selectedPatients.length > 0
                            ? caseloadSelection.selectedPatients.map(p => p.consumerId)
                            : undefined,
                        consumer_names: !SHOW_SUMMARIES && caseloadSelection.selectedPatients.length > 0
                            ? caseloadSelection.selectedPatients.map(p => p.consumer)
                            : undefined,
                        note_types: clinicalFilters.noteTypes.length ? clinicalFilters.noteTypes : undefined,
                        form_names: clinicalFilters.formNames.length ? clinicalFilters.formNames : undefined,
                        service_codes: clinicalFilters.serviceCodes.length ? clinicalFilters.serviceCodes : undefined,
                        clinical_activities: clinicalFilters.clinicalActivities.length ? clinicalFilters.clinicalActivities : undefined,
                        assessment_scales: clinicalFilters.assessmentScales.length ? clinicalFilters.assessmentScales : undefined,
                        service_programs: clinicalFilters.servicePrograms.length ? clinicalFilters.servicePrograms : undefined,
                        service_descriptions: clinicalFilters.serviceDescriptions.length ? clinicalFilters.serviceDescriptions : undefined,
                        providers: clinicalFilters.providers.length ? clinicalFilters.providers : undefined,
                        case_managers: clinicalFilters.caseManagers.length ? clinicalFilters.caseManagers : undefined,
                        assessment_staff: clinicalFilters.assessmentStaff.length ? clinicalFilters.assessmentStaff : undefined,
                        date_years: clinicalFilters.dateRanges.length ? clinicalFilters.dateRanges : undefined,
                        severity_indicators: clinicalFilters.severityIndicators.length ? clinicalFilters.severityIndicators : undefined,
                        clinical_top: clinicalTopOverride,
                        context_perspective: contextPerspective,
                        retrieval_effort: retrievalEffort,
                        sort_order: clinicalFilters.sortOrder || undefined,
                        model: selectedModel || undefined,
                        content_mode: contentMode,
                        ...(selectedNoteScope.status === "active"
                            ? {
                                  selected_note_scope: {
                                      status: "active",
                                      mode: selectedNoteScope.mode,
                                      note_ids: selectedNoteScope.mode === "selected_notes" ? selectedNoteScope.noteIds : undefined,
                                      patient_ids: selectedNoteScope.patientIds,
                                      source: "browse",
                                      source_filters: selectedNoteScope.sourceFilters,
                                      label: selectedNoteScope.label
                                  }
                              }
                            : {}),
                        ...(options?.compact ? { compact: true } : {}),
                        ...(seed !== null ? { seed: seed } : {})
                    }
                },
                session_state: currentSessionStateId ?? currentSessionState ?? null
            };

            const response = await chatApi(request, shouldStream, token);
            if (!response.body) {
                throw Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw Error(`Request failed with status ${response.status}`);
            }

            let parsedResponse: ChatAppResponse;

            if (shouldStream) {
                parsedResponse = await handleAsyncRequest(question, answers, response.body, () => {
                    firstTokenTime = Date.now();
                });
                const nextAnswers: ChatAnswer[] = [...answers, [question, parsedResponse]];
                setAnswers(nextAnswers);
                await saveHistory(nextAnswers, parsedResponse);
            } else {
                const jsonResponse: ChatAppResponseOrError = await response.json();
                if (jsonResponse.error) {
                    throw Error(jsonResponse.error);
                }
                parsedResponse = jsonResponse as ChatAppResponse;
                const nextAnswers: ChatAnswer[] = [...answers, [question, parsedResponse]];
                setAnswers(nextAnswers);
                await saveHistory(nextAnswers, parsedResponse);
            }

            // Log complete chat query with all metrics
            const responseContext = parsedResponse.context;
            const thoughtsWithUsage = responseContext?.thoughts?.find(t => t.props?.token_usage);
            const tokenUsage = thoughtsWithUsage?.props?.token_usage;

            logDevChoice(
                "chat_query",
                {
                    model: selectedModel,
                    template: selectedTemplate,
                    content_mode: contentMode,
                    ai_query_limit: clinicalFilters.topResults,
                    retrieve_count: clinicalFilters.topResults,
                    estimated_tokens: tokenEstimate?.estimatedTokens,
                    total_characters: tokenEstimate?.totalCharacters,
                    note_count: tokenEstimate?.noteCount,
                    content_mode_estimate: tokenEstimate?.contentMode,
                    utilization_percent: tokenEstimate?.utilizationPercent,
                    exceeds_limit: tokenEstimate?.exceedsLimit,
                    prompt_tokens: tokenUsage?.prompt_tokens,
                    completion_tokens: tokenUsage?.completion_tokens,
                    total_tokens: tokenUsage?.total_tokens,
                    time_to_first_token_ms: firstTokenTime ? firstTokenTime - requestStartTime : null,
                    total_response_time_ms: Date.now() - requestStartTime,
                    question_preview: question.substring(0, 50)
                },
                clinicalFilters.patId
            );

            setSpeechUrls([...speechUrls, null]);
        } catch (e) {
            setError(e);
            logDevChoice(
                "chat_error",
                {
                    model: selectedModel,
                    template: selectedTemplate,
                    content_mode: contentMode,
                    ai_query_limit: clinicalFilters.topResults,
                    estimated_tokens: tokenEstimate?.estimatedTokens,
                    note_count: tokenEstimate?.noteCount,
                    error_message: e instanceof Error ? e.message : String(e),
                    error_type: e instanceof Error && e.message.includes("429") ? "rate_limit" : "unknown",
                    question_preview: question.substring(0, 50)
                },
                clinicalFilters.patId
            );
        } finally {
            setIsLoading(false);
        }
    };

    // Clear chat
    const clearChat = () => {
        lastQuestionRef.current = "";
        setChatLastQuestion("");
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setSpeechUrls([]);
        setStreamedAnswers([]);
        setIsLoading(false);
        setIsStreaming(false);
    };

    useEffect(() => {
        if (previousSelectedNoteScopeKey.current === selectedNoteScopeKey) return;
        previousSelectedNoteScopeKey.current = selectedNoteScopeKey;
        lastQuestionRef.current = "";
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setSelectedAnswer(0);
        setStreamedAnswers([]);
        setSpeechUrls([]);
        setIsLoading(false);
        setIsStreaming(false);
        error && setError(undefined);
    }, [selectedNoteScopeKey]);

    // Settings change confirmation
    const confirmSettingChange = (type: string, apply: () => void) => {
        if (hasChatInProgress) {
            setPendingSettingChange({ type, apply });
        } else {
            apply();
        }
    };

    const handleConfirmClearChat = () => {
        if (pendingSettingChange) {
            pendingSettingChange.apply();
            clearChat();
            setPendingSettingChange(null);
        }
    };

    const handleConfirmContinue = () => {
        if (pendingSettingChange) {
            pendingSettingChange.apply();
            setPendingSettingChange(null);
        }
    };

    // Effects

    useEffect(() => {
        if (isLoading) {
            streamAutoScrollEnabled.current = true;
            scrollToStreamEnd("smooth");
        }
    }, [isLoading]);

    useEffect(() => {
        if (isStreaming) scrollToStreamEnd("auto");
    }, [streamedAnswers, isStreaming]);

    useEffect(() => {
        if (!isStreaming) streamAutoScrollEnabled.current = true;
    }, [isStreaming]);
    useEffect(() => {
        getConfig();
    }, []);

    useEffect(() => {
        updateStreamingPreference(streamingEnabled, streamingDisabledByOverrides);
    }, [streamingDisabledByOverrides, streamingEnabled]);

    // Token estimation
    useEffect(() => {
        const hasPatientForEstimate = clinicalFilters.patId || (!SHOW_SUMMARIES && caseloadSelection.selectedPatients.length > 0);
        if (!useBudgetMonitor || !hasPatientForEstimate) {
            setTokenEstimate(null);
            return;
        }

        setIsEstimatingTokens(true);

        const debounceTimer = setTimeout(async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                const response = await fetch("/estimate-tokens", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token && { Authorization: `Bearer ${token}` })
                    },
                    body: JSON.stringify({
                        consumer_id: clinicalFilters.patId,
                        consumer_ids: !SHOW_SUMMARIES && caseloadSelection.selectedPatients.length > 0
                            ? caseloadSelection.selectedPatients.map(p => p.consumerId)
                            : undefined,
                        content_mode: contentMode,
                        top: clinicalFilters.topResults,
                        model: selectedModel,
                        sort_order: clinicalFilters.sortOrder,
                        note_types: clinicalFilters.noteTypes,
                        form_names: clinicalFilters.formNames,
                        clinical_activities: clinicalFilters.clinicalActivities,
                        assessment_scales: clinicalFilters.assessmentScales,
                        service_programs: clinicalFilters.servicePrograms,
                        service_descriptions: clinicalFilters.serviceDescriptions,
                        providers: clinicalFilters.providers,
                        case_managers: clinicalFilters.caseManagers,
                        assessment_staff: clinicalFilters.assessmentStaff,
                        date_years: clinicalFilters.dateRanges,
                        severity_indicators: clinicalFilters.severityIndicators,
                        service_codes: clinicalFilters.serviceCodes
                    })
                });
                if (response.ok) {
                    setTokenEstimate(await response.json());
                }
            } catch (err) {
                console.error("Token estimation failed:", err);
            } finally {
                setIsEstimatingTokens(false);
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [
        clinicalFilters.patId,
        caseloadSelection.selectedPatients,
        clinicalFilters.topResults,
        clinicalFilters.sortOrder,
        clinicalFilters.noteTypes,
        clinicalFilters.formNames,
        clinicalFilters.clinicalActivities,
        clinicalFilters.assessmentScales,
        clinicalFilters.servicePrograms,
        clinicalFilters.serviceDescriptions,
        clinicalFilters.providers,
        clinicalFilters.caseManagers,
        clinicalFilters.assessmentStaff,
        clinicalFilters.dateRanges,
        clinicalFilters.severityIndicators,
        clinicalFilters.serviceCodes,
        contentMode,
        selectedModel,
        client,
        useBudgetMonitor
    ]);

    // Settings change handler
    const handleSettingsChange = (field: string, value: any) => {
        switch (field) {
            case "promptTemplate":
                setPromptTemplate(value);
                break;
            case "temperature":
                setTemperature(value);
                break;
            case "seed":
                setSeed(value);
                break;
            case "minimumRerankerScore":
                setMinimumRerankerScore(value);
                break;
            case "minimumSearchScore":
                setMinimumSearchScore(value);
                break;
            case "agenticReasoningEffort": {
                setRetrievalReasoningEffort(value);
                if (value === "minimal" && webSourceEnabled) {
                    setWebSourceEnabled(false);
                    setHideMinimalRetrievalReasoningOption(false);
                    updateStreamingPreference(streamingEnabled, false);
                }
                break;
            }
            case "useSemanticRanker":
                setUseSemanticRanker(value);
                break;
            case "useQueryRewriting":
                setUseQueryRewriting(value);
                break;
            case "reasoningEffort":
                setReasoningEffort(value);
                break;
            case "excludeCategory":
                setExcludeCategory(value);
                break;
            case "includeCategory":
                setIncludeCategory(value);
                break;
            case "shouldStream":
                {
                    const normalizedShouldStream = !!value;
                    forcedStreamingRef.current = false;
                    previousShouldStreamRef.current = normalizedShouldStream;
                    setShouldStream(normalizedShouldStream);
                }
                break;
            case "useSuggestFollowupQuestions":
                setUseSuggestFollowupQuestions(value);
                break;
            case "llmInputs":
                break;
            case "selectedModel":
                confirmSettingChange("Model", () => {
                    setSelectedModel(value);
                    logDevChoice("model_selection", { model: value, previous: selectedModel }, clinicalFilters.patId);
                });
                break;
            case "selectedTemplate":
                setSelectedTemplate(value);
                logDevChoice("template_selection", { template: value, previous: selectedTemplate }, clinicalFilters.patId);
                break;
            case "sendTextSources":
                setSendTextSources(value);
                break;
            case "sendImageSources":
                setSendImageSources(value);
                break;
            case "searchTextEmbeddings":
                setSearchTextEmbeddings(value);
                break;
            case "searchImageEmbeddings":
                setSearchImageEmbeddings(value);
                break;
            case "retrievalMode":
                setRetrievalMode(value);
                break;
            case "useAgenticKnowledgeBase": {
                setUseAgenticRetrieval(value);
                setUseBudgetMonitor(!value);
                if (value && contentMode === "semantic") {
                    setContentMode("full");
                }
                let effectiveWebSource = webSourceEnabled;
                if (!value && webSourceEnabled) {
                    effectiveWebSource = false;
                    setWebSourceEnabled(false);
                    setHideMinimalRetrievalReasoningOption(false);
                }
                const shouldDisableStreaming = !!value && effectiveWebSource;
                updateStreamingPreference(streamingEnabled, shouldDisableStreaming);
                break;
            }
            case "useLangGraphClinicalRetrieval":
                setUseLangGraphClinicalRetrieval(!!value);
                logDevChoice("langgraph_clinical_retrieval_toggle", { enabled: !!value }, clinicalFilters.patId);
                break;
            case "clinicalChatGraphMode": {
                const mode = value === "trace" || value === "supplemental" || value === "fusion" ? value : "off";
                setClinicalChatGraphMode(mode);
                logDevChoice("clinical_chat_graph_mode", { mode }, clinicalFilters.patId);
                break;
            }
            case "clinicalChatQueryRewriteMode": {
                const mode = value === "deterministic" || value === "llm" ? value : "auto";
                setClinicalChatQueryRewriteMode(mode);
                logDevChoice("clinical_chat_query_rewrite_mode", { mode }, clinicalFilters.patId);
                break;
            }
            case "clinicalChatRuntimePlannerMode": {
                const mode = value === "llm" ? "llm" : "deterministic";
                setClinicalChatRuntimePlannerMode(mode);
                logDevChoice("clinical_chat_runtime_planner_mode", { mode }, clinicalFilters.patId);
                break;
            }
            case "clinicalChatRerankerMode": {
                const mode = value === "lexical" ? "lexical" : "disabled";
                setClinicalChatRerankerMode(mode);
                logDevChoice("clinical_chat_reranker_mode", { mode }, clinicalFilters.patId);
                break;
            }
            case "showGraphSupplementalEvidence":
                if (!showGraphSupplementalEvidenceOption) {
                    setShowGraphSupplementalEvidence(false);
                    return;
                }
                setShowGraphSupplementalEvidence(!!value);
                logDevChoice("supplemental_graph_evidence_toggle", { enabled: !!value }, clinicalFilters.patId);
                break;
            case "useBudgetMonitor":
                setUseBudgetMonitor(!!value);
                break;
            case "contextPerspective":
                setContextPerspective(value === "whole_patient_review" ? "whole_patient_review" : "focused_rag");
                break;
            case "retrievalEffort":
                setRetrievalEffort(value === "quick" || value === "best" ? value : "balanced");
                break;
            case "useWebSource":
                if (!webSourceSupported) {
                    setWebSourceEnabled(false);
                    return;
                }
                const normalizedWebSource = !!value;
                setWebSourceEnabled(normalizedWebSource);
                setHideMinimalRetrievalReasoningOption(normalizedWebSource);
                if (normalizedWebSource) {
                    setUseSuggestFollowupQuestions(false);
                }
                const shouldDisableStreaming = useAgenticKnowledgeBase && normalizedWebSource;
                updateStreamingPreference(streamingEnabled, shouldDisableStreaming);
                break;
            case "useSharePointSource":
                if (!sharePointSourceSupported) {
                    setSharePointSourceEnabled(false);
                    return;
                }
                setSharePointSourceEnabled(!!value);
                break;
        }
    };

    // Budget option handler
    const handleBudgetOptionSelect = (option: BudgetOption) => {
        switch (option.action) {
            case "limit_notes":
                const limitValue = option.value as number;
                setClinicalFilters(prev => ({ ...prev, topResults: limitValue }));
                logDevChoice("budget_option_selected", {
                    action: "limit_notes",
                    value: limitValue,
                    previous: clinicalFilters.topResults
                });
                break;
            case "switch_mode":
                const newMode = option.value as string;
                setContentMode(newMode as "full" | "summary" | "semantic");
                logDevChoice("budget_option_selected", {
                    action: "switch_mode",
                    value: newMode,
                    previous: contentMode
                });
                break;
            case "refine_filters":
                setIsClinicalPanelOpen(true);
                logDevChoice("budget_option_selected", {
                    action: "refine_filters"
                });
                break;
        }
    };

    // Citation/analysis panel handlers
    const onExampleClicked = (example: string) => {
        makeApiRequest(example);
    };

    const onShowCitation = (citation: string, index: number) => {
        setIsClinicalPanelOpen(false);
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }
        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        setIsClinicalPanelOpen(false);
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }
        setSelectedAnswer(index);
    };

    // Return everything the layout needs
    return {
        // Translation
        t,
        i18n,

        // Core chat state
        answers,
        streamedAnswers,
        isLoading,
        isStreaming,
        error,
        lastQuestionRef,
        chatMessageStreamEnd,
        chatMessageStreamContainer,
        onChatMessageStreamScroll,

        // Actions
        makeApiRequest,
        clearChat,
        onExampleClicked,
        onShowCitation,
        onToggleTab,

        // Analysis panel
        activeCitation,
        activeAnalysisPanelTab,
        selectedAnswer,

        // Settings
        handleSettingsChange,
        confirmSettingChange,
        pendingSettingChange,
        handleConfirmClearChat,
        handleConfirmContinue,

        // Panel visibility
        isClinicalPanelOpen,
        setIsClinicalPanelOpen,
        isConfigPanelOpen,
        setIsConfigPanelOpen,
        isHistoryPanelOpen,
        setIsHistoryPanelOpen,
        isClinicalPanelPinned,
        setIsClinicalPanelPinned,

        // Token budget
        tokenEstimate,
        isEstimatingTokens,
        handleBudgetOptionSelect,
        useBudgetMonitor,

        // Content & model
        contentMode,
        setContentMode,
        selectedModel,
        availableModels,
        selectedTemplate,

        // Feature flags
        showMultimodalOptions,
        showSemanticRankerOption,
        showQueryRewritingOption,
        showReasoningEffortOption,
        showVectorOption,
        showUserUpload,
        showLanguagePicker,
        showSpeechInput,
        showSpeechOutputBrowser,
        showSpeechOutputAzure,
        showChatHistoryBrowser,
        showChatHistoryCosmos,
        showAgenticRetrievalOption,
        webSourceSupported,
        webSourceEnabled,
        sharePointSourceSupported,
        sharePointSourceEnabled,
        useAgenticKnowledgeBase,
        useLangGraphClinicalRetrieval,
        clinicalChatGraphMode,
        clinicalChatQueryRewriteMode,
        clinicalChatRuntimePlannerMode,
        clinicalChatRerankerMode,
        showGraphSupplementalEvidenceOption,
        showGraphSupplementalEvidence,
        contextPerspective,
        retrievalEffort,
        hideMinimalRetrievalReasoningOption,

        // Speech
        speechConfig,

        // Streaming
        shouldStream,
        streamingDisabledByOverrides,

        // History
        historyProvider,
        historyManager,

        // Auth
        client,
        loggedIn,
        requireAccessControl,
        useLogin: !!useLogin,
        streamingEnabled,

        // History restore
        restoreChat: (restoredAnswers: unknown) => {
            const normalizedAnswers = normalizeHistoryAnswers(restoredAnswers);
            if (normalizedAnswers.length === 0) return;
            setAnswers(normalizedAnswers);
            setStreamedAnswers([]);
            setSpeechUrls(normalizedAnswers.map(() => null));
            setSelectedAnswer(0);
            setActiveCitation(undefined);
            setActiveAnalysisPanelTab(undefined);
            setIsLoading(false);
            setIsStreaming(false);
            error && setError(undefined);
            const lastQuestion = normalizedAnswers[normalizedAnswers.length - 1][0];
            lastQuestionRef.current = lastQuestion;
            setChatLastQuestion(lastQuestion);
            setIsHistoryPanelOpen(false);
        },

        // Dev settings (for Settings panel)
        promptTemplate,
        temperature,
        seed,
        minimumRerankerScore,
        minimumSearchScore,
        agenticReasoningEffort,
        retrievalMode,
        useSemanticRanker,
        useQueryRewriting,
        reasoningEffort,
        useSuggestFollowupQuestions,
        searchTextEmbeddings,
        searchImageEmbeddings,
        sendTextSources,
        sendImageSources,
        includeCategory,
        excludeCategory,

        // Clinical
        clinicalFilters,
        setClinicalFilters,
        selectedNoteScope,
        clearSelectedNoteScope
    };
}

export type ChatSessionReturn = ReturnType<typeof useChatSession>;
